import { useMemo, useState } from "react"

import { useScopedStorageKey } from "@/workspaces/scope"
import { Button } from "@coval/ui"
import { FileText } from "lucide-react"

import { EntityPickerDialog } from "@/components/entity-picker/EntityPickerDialog"
import { EntitySelectionCard } from "@/components/entity-picker/EntitySelectionCard"

import { useListTestCasesTransport } from "../transport"
import { groupTestCaseSelection } from "../utils/testCaseSelection"
import { useTestCaseLibraryPickerProps } from "./TestCaseLibraryPickerProps"

interface Props {
  selectedIds: string[]
  onChange: (ids: string[], testSetIds: string[]) => void
  disabled?: boolean
  singleSelect?: boolean
  hideCard?: boolean
  onBrowse?: () => void
  hidePicker?: boolean
}

export function TestCaseLibraryPicker(props: Props) {
  const scope = useScopedStorageKey("test-case-library-picker")
  return <ScopedTestCaseLibraryPicker key={scope} {...props} />
}

function ScopedTestCaseLibraryPicker({
  selectedIds,
  onChange,
  disabled = false,
  singleSelect = false,
  hideCard = false,
  onBrowse,
  hidePicker = false,
}: Props) {
  const [open, setOpen] = useState(false)
  const pickerOpen = open && !disabled && !hidePicker
  const catalog = useListTestCasesTransport({
    enabled: selectedIds.length > 0 || pickerOpen,
  })
  const [draft, setDraft] = useState<string[]>([])
  const closePicker = () => setOpen(false)
  const casePickerProps = useTestCaseLibraryPickerProps({
    selectedIds: draft,
    onSelectionChange: setDraft,
    singleSelect,
    enabled: pickerOpen,
  })
  const records = catalog.data
  const byId = useMemo(
    () => new Map(records?.map((record) => [record.id, record]) ?? []),
    [records],
  )
  const ready = records !== undefined && !catalog.error
  const applySelection = (ids: string[]) => {
    if (disabled || (ids.length > 0 && !ready)) {
      return false
    }
    const grouped = groupTestCaseSelection(ids, records ?? [])
    onChange(grouped.selectedIds, grouped.testSetIds)
    return true
  }
  const unavailable =
    records !== undefined && selectedIds.some((id) => !byId.has(id))

  return (
    <>
      {!hideCard && (
        <EntitySelectionCard
          icon={FileText}
          title="Test Cases"
          chips={selectedIds.map((id) => ({
            id,
            label: byId.has(id)
              ? `${byId.get(id)?.description || "No description"} (${byId.get(id)?.test_set_name})`
              : id,
            removeLabel: `Remove test case ${id}`,
            isMissing: records !== undefined && !byId.has(id),
          }))}
          onRemove={
            ready
              ? (id) =>
                  applySelection(
                    selectedIds.filter((selected) => selected !== id),
                  )
              : undefined
          }
          onClearAll={() => applySelection([])}
          onBrowse={
            onBrowse ??
            (() => {
              setDraft(selectedIds)
              setOpen(true)
            })
          }
          browseLabel="Browse Test Cases"
          addLabel="Add Test Cases"
          browseTestId="test-case-library-browse-button"
          disabled={disabled}
          countLabel={`${selectedIds.length} / 100 selected`}
        >
          {unavailable && (
            <p role="alert" className="text-sm text-coval-danger-text">
              Some selected cases are unavailable. Remove them or retry before
              continuing.
            </p>
          )}
          {catalog.error && (
            <div role="alert" className="text-sm text-coval-danger-text">
              Could not load test cases.{" "}
              <Button
                variant="secondary"
                onClick={() => void catalog.refetch()}
              >
                Retry
              </Button>
            </div>
          )}
          {catalog.isLoading && (
            <p role="status" className="text-sm text-coval-text-muted">
              Loading test cases…
            </p>
          )}
        </EntitySelectionCard>
      )}
      {!hidePicker && (
        <EntityPickerDialog
          {...casePickerProps}
          isOpen={pickerOpen}
          onCancel={closePicker}
          onApply={() => {
            if (applySelection(draft)) {
              closePicker()
            }
          }}
        />
      )}
    </>
  )
}
